/**
 * Public technology page (/tech/<university>/<title> by default — the URL
 * pattern is the server's and admin-editable).
 *
 * Renders exactly what GET /api/v1/pages/by-path returned for this visitor's
 * state: sections arrive in order, hidden ones are absent, and locked ones
 * carry no data (the page shows blurred neutral filler under a "sign in" or
 * "upgrade" button). Left column: summary, TRL, value levers, researchers &
 * IP, problem & applications. Right column: university details, industry
 * insights, similar technologies (single column on phones, cards last).
 *
 * CTAs never touch the legacy /api/v1/technologies/{id} endpoint: Contact
 * University opens login (signed out) or a panel that hands over to Cosmos.
 */

import React, { useEffect, useRef, useState } from 'react';
import { flagSrc, COUNTRY_ISO } from '../NewHome';
import {
  IndustryInsightsData, PageSection, PageState, ProblemApplicationsData, ResearchersIpData, SimilarTechData,
  SummaryData, TechPageData, TrlData, UniversityDetailsData, ValueLeversData, setBookmark,
} from './api';
import { useTpShell } from './PublicShell';
import { usePageSeo } from './seo';
import {
  BookmarkIcon, Breadcrumbs, copyText, LockedBlock, PreviewSwitch, ShareIcon, TRL_LABELS, useToast,
} from './shared';
import { tpTrack, useEngagement, useScrollDepth, useSectionViews } from './track';
import { TpUpgradeSheet, UpgradeAsk } from './UpgradeSheet';
import ContactTTOModal from '../../../components/modals/ContactTTOModal';
import { IpIntelOrderModal } from '../../../components/techcard/IpIntelOrderModal';
import { setCosmosSeed } from '../../../utils/cosmosSeed';
import { apiService } from '../../../services/apiService';
import { cachedShareUrl, channelHref, copyWhenReady, ShareChannel, shareLinkFor, ShareTarget, urlForChannel } from './shareLinks';

const SECTION_TITLES: Record<string, string> = {
  summary: 'Summary',
  trl: 'Technology readiness',
  value_levers: 'Value levers',
  researchers_ip: 'Researchers & IP',
  problem_applications: 'Problem & applications',
  university_details: 'University details',
  industry_insights: 'Industry insights',
  similar_tech: 'Similar technologies',
};

/** What a locked section is called in its button ("Sign up to view the …"). */
const SECTION_NOUN: Record<string, string> = {
  summary: 'summary',
  trl: 'readiness level',
  value_levers: 'value levers',
  researchers_ip: 'researchers & IP',
  problem_applications: 'problem & applications',
  university_details: 'university details',
  industry_insights: 'industry insights',
  similar_tech: 'similar technologies',
};

const RIGHT = new Set(['university_details', 'industry_insights', 'similar_tech']);


interface Props {
  page: TechPageData;
  busy: boolean;
  onPreview: (s: PageState) => void;
}

export const TechPage: React.FC<Props> = ({ page, busy, onPreview }) => {
  const shell = useTpShell();
  const techId = page.id;
  const state = page.state;
  const signedInView = state !== 'pre_login';
  const ctas = page.ctas || { contact_university: true, ip_assessment: true, share: true, bookmark: true };
  // Paid: the Contact university sheet Cosmos uses (office e-mail, copy,
  // concierge). Not paid: the plans sheet, here (UpgradeSheet). IP Intel:
  // the order sheet, here — all without leaving the page (owner, 2026-09-29).
  const [contactOpen, setContactOpen] = useState(false);
  const [upgradeAsk, setUpgradeAsk] = useState<UpgradeAsk | null>(null);
  const [ipOpen, setIpOpen] = useState(false);
  // Who is asking, for the Contact university sheet's Talk to Concierge lead
  // (Admin → Leads → Talk to Concierge), which needs the account's name and
  // e-mail — so they are fetched when a paying viewer lands, before the
  // sheet can open. The opening itself is recorded by the server.
  const [me, setMe] = useState<{ name: string; email: string } | null>(null);
  useEffect(() => {
    if (state !== 'paid' || me) return;
    const uid = apiService.getStoredUserId?.() || '';
    if (!uid) return;
    apiService.getProfile(uid).then(res => {
      const u: any = (res as any)?.data?.user || (res as any)?.data || {};
      const email = String(u.email || '').trim();
      if (email) setMe({ name: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || email, email });
    }).catch(() => { /* the sheet works without it */ });
  }, [state, me]);
  const [toast, showToast] = useToast();

  usePageSeo(page.seo);

  const viewed = useRef('');
  useEffect(() => {
    if (viewed.current === techId) return;
    viewed.current = techId;
    tpTrack('techpage_view', { tech_id: techId, state });
  }, [techId, state]);
  useSectionViews('techpageSection', 'techpage_section_view', techId, (section, el) => ({
    section, mode: el.dataset.mode || '', tech_id: techId, state,
  }));
  useScrollDepth('techpage_scroll_depth', techId, { tech_id: techId });
  useEngagement(techId);

  const cta = (name: string, position: string) =>
    tpTrack('techpage_cta_click', { cta: name, position, tech_id: techId, state });

  const promptLogin = (reason: string) => {
    tpTrack('techpage_login_prompt', { reason, tech_id: techId });
    shell.openLogin(reason);
  };

  const upgrade = (from: string, text?: { headline?: string; message?: string }) => {
    tpTrack('techpage_upgrade_click', { from, tech_id: techId });
    setUpgradeAsk({ feature: `tech page: ${from}`, ...(text || {}) });
  };

  const onLocked = (s: PageSection) => {
    const reason = s.lock?.reason || (signedInView ? 'upgrade' : 'sign_in');
    tpTrack('techpage_locked_click', { section: s.id, reason, tech_id: techId, state });
    if (reason === 'sign_in') promptLogin(`locked_${s.id}`);
    else upgrade(`locked_${s.id}`);
  };

  const onContact = (position: string) => {
    cta('contact_university', position);
    if (!signedInView) {
      tpTrack('techpage_login_prompt', { reason: 'contact_university', tech_id: techId });
      shell.openLogin('contact_university', undefined, {
        heading: 'Sign up to Contact University',
        subheading: 'Sign in or create a free account to reach the technology transfer office.',
      });
      return;
    }
    if (state !== 'paid') {
      upgrade('contact_university', {
        headline: 'Upgrade to Contact University',
        message: 'Voyager gives you the technology transfer office\'s contact for every technology, and our concierge to support the conversation.',
      });
      return;
    }
    tpTrack('techpage_contact_open', { tech_id: techId, state });
    setContactOpen(true);
  };

  const uniClick = (position: string) => (e: React.MouseEvent) => {
    tpTrack('techpage_university_click', { code: page.university?.code, position, tech_id: techId });
    if (page.university?.path) shell.go(page.university.path, e);
  };


  const uni = page.university || ({} as TechPageData['university']);
  const uniName = uni.label || uni.name;
  const left = (page.sections || []).filter(s => !RIGHT.has(String(s.id)) && s.mode !== 'hidden');
  const right = (page.sections || []).filter(s => RIGHT.has(String(s.id)) && s.mode !== 'hidden');

  const renderSection = (s: PageSection, asCard: boolean) => {
    const id = String(s.id);
    const titleId = `tpg-sec-${id}`;
    const noun = SECTION_NOUN[id] || 'this section';
    const locked = s.mode === 'locked';
    const fallback = (s.lock?.reason || (signedInView ? 'upgrade' : 'sign_in')) === 'sign_in'
      ? (noun === 'summary' ? 'Sign up to view full summary' : `Sign up to view the ${noun}`) : `Upgrade to unlock the ${noun}`;
    return (
      <section
        key={id}
        className={asCard ? 'tpg-card' : 'tpg-section'}
        data-techpage-section={id}
        data-mode={s.mode}
        aria-labelledby={titleId}
      >
        <h2 className="tpg-eyebrow" id={titleId}>{SECTION_TITLES[id] || id.replace(/_/g, ' ')}</h2>
        {locked ? (
          <LockedBlock
            seed={`${techId}:${id}`}
            lock={s.lock}
            fallbackMessage={fallback}
            lines={asCard ? 4 : id === 'trl' ? 3 : 7}
            onClick={() => onLocked(s)}
          />
        ) : (
          <SectionBody s={s} page={page} onUpgradeSummary={() => {
            if (!signedInView) promptLogin('summary_short');
            else upgrade('summary_short');
          }} />
        )}
      </section>
    );
  };

  return (
    <div className="tpg-wrap tpg-page">
      {page.admin_preview?.allowed && (
        <PreviewSwitch
          states={page.admin_preview.states || []}
          current={state}
          busy={busy}
          onPick={s => { tpTrack('techpage_admin_state_switch', { to: s, tech_id: techId }); onPreview(s); }}
        />
      )}

      {page.ui?.breadcrumbs && (
        <Breadcrumbs
          items={page.breadcrumbs || []}
          onClick={(level, path, e) => { tpTrack('techpage_breadcrumb_click', { level, tech_id: techId }); shell.go(path, e); }}
        />
      )}

      {/* One colour (owner, 2026-09-29): the brown accent on the last words
          (page.title_accent_from) is no longer drawn. */}
      <h1 className="tpg-h1">{page.title}</h1>

      <div className="tpg-actions">
        {ctas.contact_university && (
          <button type="button" className="tpg-btn-primary" onClick={() => onContact('header')}>Contact University</button>
        )}
        {ctas.ip_assessment && (
          <button
            type="button"
            className="tpg-btn-secondary"
            onClick={() => { cta('ip_assessment', 'header'); setIpOpen(true); }}
          >Get IP Assessment</button>
        )}
        {ctas.share && <ShareButton page={page} onToast={showToast} onOpen={() => cta('share', 'header')} />}
        {ctas.bookmark && (
          <BookmarkButton
            page={page}
            signedInView={signedInView}
            onPrompt={() => { cta('bookmark', 'header'); promptLogin('bookmark'); }}
            onClickTracked={() => cta('bookmark', 'header')}
            onToast={showToast}
          />
        )}
      </div>

      <hr className="tpg-divider" />

      <div className={`tpg-grid${busy ? ' tpg-busy' : ''}`} aria-busy={busy || undefined}>
        <div>{left.map(s => renderSection(s, false))}</div>
        {right.length > 0 && <aside className="tpg-aside" aria-label="About this technology">{right.map(s => renderSection(s, true))}</aside>}
      </div>

      <ContactTTOModal
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
        techId={techId}
        techTitle={page.title}
        university={page.university?.name}
        userName={me?.name}
        userEmail={me?.email}
        surface="tech_page"
      />
      {upgradeAsk && <TpUpgradeSheet {...upgradeAsk} techId={techId} onClose={() => setUpgradeAsk(null)} />}
      {ipOpen && <IpIntelOrderModal techId={techId} techTitle={page.title} entry="techpage" onClose={() => setIpOpen(false)} />}
      {toast}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section bodies
// ---------------------------------------------------------------------------

const SectionBody: React.FC<{ s: PageSection; page: TechPageData; onUpgradeSummary: () => void }> = ({ s, page, onUpgradeSummary }) => {
  const shell = useTpShell();
  const techId = page.id;
  const id = String(s.id);
  const d: any = s.data || {};

  if (id === 'summary') {
    const sd = d as SummaryData;
    const text = (sd.text || '').trim();
    if (!text) return <p className="tpg-muted">No summary has been published for this technology yet.</p>;
    if (sd.is_short) {
      return (
        <>
          <div className="tpg-fade"><p className="tpg-body">{text}</p></div>
          <button type="button" className="tpg-pill" onClick={onUpgradeSummary}>
            <span aria-hidden="true">🔒</span>
            <span>{page.state === 'pre_login'
              ? 'You’re viewing a short summary — sign in to read more'
              : 'You’re viewing a short summary — upgrade to read the full summary'}</span>
          </button>
        </>
      );
    }
    return <p className="tpg-body">{text}</p>;
  }

  if (id === 'trl') return <TrlCard d={d as TrlData} techId={techId} />;

  if (id === 'value_levers') {
    // The technical tag only ("Higher Process Yield"), never "Lever · Tag";
    // a tag shared by two levers shows once.
    const seen = new Set<string>();
    const items = ((d as ValueLeversData).items || [])
      .map(it => typeof it === 'string' ? (it.split(/\s*(?:->|→|·|:)\s*/).pop() || it) : (it?.tag || it?.label || it?.lever || ''))
      .map(t => t.trim())
      .filter(t => t && !seen.has(t.toLowerCase()) && seen.add(t.toLowerCase())) as string[];
    if (!items.length) return <p className="tpg-muted">Value levers for this technology are being prepared.</p>;
    return <div className="tpg-chips">{items.map((t, i) => <span key={`${i}:${t}`} className="tpg-chip">{t}</span>)}</div>;
  }

  if (id === 'researchers_ip') {
    const r = d as ResearchersIpData;
    const names = (r.researchers || []).filter(Boolean);
    const patents = (r.patents || []).filter(p => p && (p.number || p.type || p.country));
    const any = names.length || r.ip_summary || r.publications_summary || patents.length;
    if (!any) return <p className="tpg-muted">No researcher or IP details have been published for this technology.</p>;
    return (
      <>
        {names.length > 0 && (
          <div className="tpg-sub">
            <h3 className="tpg-h3">Researchers</h3>
            <ul className="tpg-names">{names.map((n, i) => <li key={`${i}:${n}`}>{n}</li>)}</ul>
          </div>
        )}
        {r.ip_summary && (
          <div className="tpg-sub">
            <h3 className="tpg-h3">IP Summary</h3>
            <p className="tpg-body">{r.ip_summary}</p>
          </div>
        )}
        {patents.length > 0 && (
          <div className="tpg-sub">
            <h3 className="tpg-h3">Patents</h3>
            <table className="tpg-patents">
              <thead><tr><th>Number</th><th>Type</th><th>Country</th></tr></thead>
              <tbody>{patents.map((p, i) => <tr key={i}><td>{p.number || '—'}</td><td>{p.type || '—'}</td><td>{p.country || '—'}</td></tr>)}</tbody>
            </table>
          </div>
        )}
        {r.publications_summary && (
          <div className="tpg-sub">
            <h3 className="tpg-h3">Publications Summary</h3>
            <p className="tpg-body">{r.publications_summary}</p>
          </div>
        )}
      </>
    );
  }

  if (id === 'problem_applications') {
    const p = d as ProblemApplicationsData;
    const apps = (p.example_applications || []).filter(Boolean);
    if (!p.problem_addressed && !p.problem_evidence && !apps.length) {
      return <p className="tpg-muted">Problem and application insights for this technology are being prepared.</p>;
    }
    return (
      <>
        {p.problem_addressed && (
          <div className="tpg-sub">
            <h3 className="tpg-h3">Problem addressed</h3>
            <p className="tpg-body">{p.problem_addressed}</p>
          </div>
        )}
        {p.problem_evidence && (
          <div className="tpg-sub">
            <h3 className="tpg-h3">Problem evidence</h3>
            <p className="tpg-body">{p.problem_evidence}</p>
          </div>
        )}
        {apps.length > 0 && (
          <div className="tpg-sub">
            <h3 className="tpg-h3">Example applications</h3>
            <ul className="tpg-list">{apps.map((a, i) => <li key={`${i}:${a}`}>{a}</li>)}</ul>
          </div>
        )}
      </>
    );
  }

  if (id === 'university_details') {
    const u = d as UniversityDetailsData;
    const name = u.name || page.university?.name;
    const path = u.path || page.university?.path;
    return (
      <>
        {path ? (
          <a
            className="tpg-card-title"
            href={path}
            onClick={e => { tpTrack('techpage_university_click', { code: page.university?.code, position: 'card', tech_id: techId }); shell.go(path, e); }}
          >{name}</a>
        ) : <p className="tpg-card-title">{name}</p>}
        {u.label && u.label !== name && <p className="tpg-card-line">{u.label}</p>}
        {u.country && (
          <p className="tpg-card-line" style={{ marginTop: 4 }}>
            {COUNTRY_ISO[u.country] && <img className="tpg-flag" src={flagSrc(u.country)} alt="" width={18} height={13} loading="lazy" />}
            {u.country}
          </p>
        )}
      </>
    );
  }

  if (id === 'industry_insights') {
    const ii = d as IndustryInsightsData;
    if (!ii.industry) return <p className="tpg-muted">Industry insights for this technology are being prepared.</p>;
    const n = typeof ii.count === 'number' && ii.count > 0 ? ii.count.toLocaleString('en-US') : '';
    // Opens Cosmos with this industry's filter on (utils/cosmosSeed); a
    // visitor who is not signed in signs in first and lands there.
    const openInCosmos = (e: React.MouseEvent) => {
      e.preventDefault();
      tpTrack('techpage_industry_click', { industry: ii.industry, tech_id: techId });
      setCosmosSeed({ industry: ii.industry || undefined, source: 'techpage_industry_insights' });
      if (page.state === 'pre_login') {
        shell.openLogin('industry_insights', '/', {
          heading: `Explore ${ii.industry} in Cosmos`,
          subheading: 'Sign in or create a free account to browse them.',
        });
      } else {
        window.location.href = '/';
      }
    };
    return (
      <a
        className="tpg-insight"
        href="/"
        onClick={openInCosmos}
      >
        Discover {n ? <strong>{n}</strong> : 'more'} technologies from <strong>{ii.industry}</strong> →
      </a>
    );
  }

  if (id === 'similar_tech') {
    const items = ((d as SimilarTechData).items || []).filter(it => it && it.path && it.title);
    if (!items.length) return <p className="tpg-muted">Similar technologies are being prepared.</p>;
    return (
      <ul className="tpg-similar">
        {items.map((it, i) => (
          <li key={it.path}>
            <a
              href={it.path}
              onClick={e => { tpTrack('techpage_similar_click', { target_id: it.id || it.path, rank: i + 1, tech_id: techId }); shell.go(it.path, e); }}
            >
              <span className="tpg-similar-title">{it.title}</span>
            </a>
          </li>
        ))}
      </ul>
    );
  }

  return null;
};

/** tech_pages.trl_block: bold title ("TRL 6" / "TRL not found"), the level's
 *  description (or, for an inferred TRL, the evidence statement), and the
 *  provenance category's wording as the small grey note. */
const TrlCard: React.FC<{ d: TrlData; techId: string }> = ({ d }) => {
  const level = typeof d.level === 'number' && d.level >= 1 && d.level <= 9 ? d.level : null;
  const title = d.title || d.label || (level ? `TRL ${level}` : d.pill === 'TRL —' || !d.pill ? 'TRL not found' : d.pill);
  const desc = d.description || (level ? TRL_LABELS[level] : '');
  return (
    <div className="tpg-trl" data-trl-category={d.category || undefined}>
      <p className="tpg-trl-level">{title}</p>
      {desc && <p className="tpg-trl-desc">{desc}</p>}
      {d.note && <p className="tpg-trl-note">{d.note}</p>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Share + bookmark
// ---------------------------------------------------------------------------

const SHARE_CHANNELS: Array<{ channel: ShareChannel; label: string }> = [
  { channel: 'linkedin', label: 'LinkedIn' },
  { channel: 'whatsapp', label: 'WhatsApp' },
  { channel: 'x', label: 'X (Twitter)' },
  { channel: 'email', label: 'E-mail' },
];

/**
 * Share: the native sheet where the device has one, else a menu (copy link,
 * LinkedIn, WhatsApp, X, e-mail). Every link shared is the visitor's own
 * tracked one (POST /api/v1/share-links, surface 'techpage'), so Admin → Tech
 * URL pages → Shares can tell who shared, through which channel, and who
 * opened it. When the server cannot mint one, the plain page URL is shared.
 */
const ShareButton: React.FC<{ page: TechPageData; onToast: (m: string) => void; onOpen: () => void }> = ({ page, onToast, onOpen }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const canonical = page.canonical_url || (typeof window !== 'undefined' ? window.location.href.split(/[?#]/)[0] : '');
  const title = page.title;
  const target: ShareTarget = { target_kind: 'tech', target_id: page.id, surface: 'techpage' };
  // Re-read after each share, so the menu's links carry the code once there is one.
  const [cached, setCached] = useState<string | null>(() => cachedShareUrl(target));
  const track = (channel: string) => tpTrack('techpage_share', { channel, tech_id: page.id });
  const linkFor = (channel: ShareChannel) => {
    const l = shareLinkFor(target, channel, canonical);
    void l.ready.then(() => setCached(cachedShareUrl(target)));
    return l;
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const onClick = async () => {
    onOpen();
    const nav: any = typeof navigator !== 'undefined' ? navigator : null;
    if (!open && nav && typeof nav.share === 'function') {
      const l = linkFor('native');
      const url = l.now || await l.ready;
      try {
        await nav.share({ title, url });
        track('native');
        return;
      } catch (e: any) {
        if (e?.name === 'AbortError') return;   // the visitor closed the sheet
        /* share sheet unavailable here (or the click's permission ran out): the menu */
      }
    }
    setOpen(o => !o);
  };

  const copy = async () => {
    const l = linkFor('copy');
    const ok = l.now ? await copyText(l.now) : await copyWhenReady(l.ready);
    track('copy');
    onToast(ok ? 'Link copied' : 'Could not copy — the link is in the address bar');
    setOpen(false);
  };

  // With a code cached the link is ready and the anchor just opens; the first
  // time, the new tab opens inside the click and is pointed at the link when
  // it arrives (a pop-up opened later would be blocked).
  const onChannel = (channel: ShareChannel) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    track(channel);
    setOpen(false);
    const l = linkFor(channel);
    if (l.now) return;
    e.preventDefault();
    const w = channel === 'email' ? null : window.open('', '_blank');
    if (w) { try { w.opener = null; } catch { /* ignore */ } }
    void l.ready.then(url => {
      const href = channelHref(channel, url, title);
      if (channel === 'email') { window.location.href = href; return; }
      if (w && !w.closed) w.location.href = href;
      else window.open(href, '_blank', 'noopener,noreferrer');
    });
  };

  return (
    <div className="tpg-share" ref={ref}>
      <button type="button" className="tpg-icon-btn" aria-label="Share this technology" aria-haspopup="menu" aria-expanded={open} onClick={onClick}>
        <ShareIcon />
      </button>
      {open && (
        <div className="tpg-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => void copy()}>Copy link</button>
          {SHARE_CHANNELS.map(l => (
            <a
              key={l.channel}
              role="menuitem"
              href={channelHref(l.channel, cached ? urlForChannel(cached, l.channel) : canonical, title)}
              target={l.channel === 'email' ? undefined : '_blank'}
              rel="noopener noreferrer"
              onClick={onChannel(l.channel)}
            >{l.label}</a>
          ))}
        </div>
      )}
    </div>
  );
};

const BookmarkButton: React.FC<{
  page: TechPageData;
  signedInView: boolean;
  onPrompt: () => void;
  onClickTracked: () => void;
  onToast: (m: string) => void;
}> = ({ page, signedInView, onPrompt, onClickTracked, onToast }) => {
  const [on, setOn] = useState(!!page.bookmarked);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOn(!!page.bookmarked); }, [page.id, page.bookmarked]);

  const click = async () => {
    if (!signedInView) { onPrompt(); return; }
    if (saving) return;
    onClickTracked();
    const next = !on;
    setOn(next);
    setSaving(true);
    const ok = await setBookmark(page.id, next);
    setSaving(false);
    if (!ok) { setOn(!next); onToast('Could not update your bookmark — please try again.'); return; }
    tpTrack('techpage_bookmark', { on: next, tech_id: page.id });
    onToast(next ? 'Saved to your bookmarks' : 'Removed from your bookmarks');
  };

  return (
    <button
      type="button"
      className="tpg-icon-btn"
      aria-label={on ? 'Remove bookmark' : 'Bookmark this technology'}
      aria-pressed={signedInView ? on : undefined}
      onClick={click}
      disabled={saving}
    >
      <BookmarkIcon filled={signedInView && on} />
    </button>
  );
};


export default TechPage;
